import { useContext, useEffect, useRef, useState } from "react";
import Header from "../../Components/Header";
import { useLocation, useNavigate } from "react-router-dom";
import "./home.css";
import PortalContext from "../PortalContext";
import { useGetTranslation } from "../../Components/Translate";
import { sendPostRequest } from "../../Client/index.js";
import ClipLoader from "react-spinners/ClipLoader";

export default function Home() {
  const { portal } = useContext(PortalContext);

  const getTranslation = useGetTranslation();

  const [failureLoading, setFailureLoading] = useState(false);

  const location = useLocation();

  const [notice, setNotice] = useState({
    show: Boolean(location.state?.notice),
    text: location.state?.notice || "",
  });

  const navigate = useNavigate();

  const orderNumberRef = useRef();
  const emailRef = useRef();
  const postcodeRef = useRef();
  const voucherRef = useRef();
  const noticeRef = useRef(null);

  const cleanQueryValue = (value, maxLength = 100) => {
    if (!value) return "";
  
    return value
      .replace(/[<>`"'\\]/g, "") // stripping common XSS characters
      .trim()
      .slice(0, maxLength);
  };
  
  const queryParams = new URLSearchParams(window.location.search);
  
  const queryOrder = cleanQueryValue(queryParams.get("order"), 50);
  const queryEmail = cleanQueryValue(queryParams.get("email"), 100);
  const queryPostcode = cleanQueryValue(queryParams.get("postcode"), 20).toUpperCase();

  useEffect(() => {
    // Clear the previous context if you are navigating back to the home page
    portal.step2 = null;
    portal.step3 = null;
    portal.step3manual = null;
    portal.step4 = null;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (failureLoading) return;
    setFailureLoading(true);

    const requestBody = {
      orderNumber: orderNumberRef.current.value,
      email: portal?.data?.settings.defaultEmail
        ? portal?.data?.settings.defaultEmail
        : emailRef.current.value,
      postcode: postcodeRef.current.value,
      voucher: voucherRef.current ? voucherRef.current.value : "",
    };

    portal.step1 = requestBody;

    // checking for previous failures

    const res = await sendPostRequest(
      "api/v2-label-portal-check-previous-failure.php",
      {
        orderNumber: orderNumberRef.current.value,
        email: portal?.data?.settings.defaultEmail
          ? portal?.data?.settings.defaultEmail
          : emailRef.current.value,
      }
    );

    setFailureLoading(false);

    if (res?.count === 0) {
      // navigate(`/step-2`, {
      //   replace: true,
      // });
      navigate(`/step-2`);
    } else {
      setNotice({
        show: true,
        text:
          res?.reason ||
          "We’ve found a previous attempt for this order. We’ll email you when your label is ready.",
      });
    }
  };

  return (
    <>
      <Header />

      {portal?.data?.settings?.isActive ? (
        <>
          <section class="page3-slider">
            <div class="container">
              <div class="row d-flex align-items-center mt-3">
                <div class="col-lg-5 col-12 pb-5">
                  <h2>
                    {getTranslation(
                      "STEP1_HEADER",
                      "Create your {tradingName} return",
                      {
                        tradingName: portal?.data?.settings?.tradingName,
                      }
                    )}
                  </h2>
                  <p class="pt-3 text-muted mb-4">
                    {getTranslation(
                      "STEP1_CUSTOMER_MESSAGE",
                      portal?.data?.settings?.custMsgLine,
                    )}                    
                  </p>
                  {notice.show && (
                    <div className="row mt-2 mb-2" ref={noticeRef}>
                      <div className="col-sm-12">
                        <div
                          role="alert"
                          aria-live="polite"
                          style={{
                            backgroundColor: "#f8d7da",
                            border: "1px solid #f5c2c7",
                            color: "#842029",
                            borderRadius: 8,
                            padding: "12px 14px",
                            width: "100%",
                            display: "block",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 500,
                              fontSize: 12,
                              wordBreak: "break-word",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {notice.text}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <form class="align-items-center" onSubmit={handleSubmit}>
                    <div class="row">
                      <div class="col-sm-12">
                        <input
                          type="text"
                          placeholder={getTranslation(
                            "STEP1_PLACEHOLDER_ORDER_NUMBER",
                            "Order number"
                          )}
                          name="orderNumber"
                          id="orderNumber"
                          ref={orderNumberRef}
                          defaultValue={portal?.step1?.orderNumber || queryOrder}
                          required
                        ></input>
                      </div>
                    </div>

                    {!portal.data.settings.defaultEmail && (
                      <div class="row mt-2">
                        <div class="col-sm-12 h-20">
                          <input
                            id="email"
                            type="email"
                            name="email"
                            placeholder={getTranslation(
                              "STEP1_PLACEHOLDER_EMAIL",
                              "Email address"
                            )}
                            ref={emailRef}
                            defaultValue={portal?.step1?.email || queryEmail}
                            required
                          ></input>
                        </div>
                      </div>
                    )}

                    <div class="row mt-2">
                      <div class="col-sm-12">
                        <input
                          type="text"
                          placeholder={getTranslation(
                            "STEP1_PLACEHOLDER_POSTCODE",
                            "Postcode / Zip code"
                          )}
                          name="postcode"
                          id="postcode"
                          ref={postcodeRef}
                          defaultValue={portal?.step1?.postcode || queryPostcode}
                          required
                          onInput={(e) => {
                            e.target.value = e.target.value.toUpperCase();
                          }}
                          autoCapitalize="characters"
                          autoCorrect="off"
                          spellCheck={false}
                        ></input>
                      </div>
                    </div>

                    {portal.data.settings.voucher.visible && (
                      <div class="row mt-2" id="voucher">
                        <div class="col-sm-12">
                          <input
                            type="text"
                            placeholder={getTranslation(
                              "STEP1_PLACEHOLDER_VOUCHER",
                              "Voucher code (if applicable)"
                            )}
                            name="voucher"
                            id="voucher"
                            ref={voucherRef}
                            defaultValue={portal?.step1?.voucher}
                          ></input>
                        </div>
                      </div>
                    )}

                    <div class="row mt-4">
                      <div class="col-sm-12">
                        <button
                          type="submit"
                          disabled={failureLoading}
                          aria-busy={failureLoading ? "true" : "false"}
                          style={{
                            backgroundColor: `${portal.data.settings.colours.primary}`,
                            opacity: failureLoading ? 0.8 : 1,
                            cursor: failureLoading ? "not-allowed" : "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            minHeight: 44,
                            minWidth: 160,
                          }}
                        >
                          {failureLoading ? (
                            <>
                              <ClipLoader size={16} color="#fff" />
                            </>
                          ) : (
                            getTranslation("CONTINUE_BUTTON", "Continue")
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>

                <div class="col-lg-1"></div>

                <div class="col-lg-6 col-12 text-center">
                  {portal?.data?.settings?.imageUrl && (
                    <img
                      src={portal?.data?.settings?.imageUrl}
                      class="w-100 img-fluid"
                      alt="Retailer Image"
                    />
                  )}

                  <div class="d-flex justify-content-end">
                    <img
                      src={require("../../Assets/Images/powered-by.png")}
                      class="img-fluid powered-home-logo mt-2 mb-2"
                    ></img>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : (
        <div class="container text-center mt-5 p-5">
          <h1>Sorry, this portal is unavailable</h1>
          <p>
            Please contact the seller directly through the marketplace you made
            the purchase or if alternative contact details are available these
            are shown below.
          </p>
          <p style={{ fontWeight: "bold", color: "#d79624" }}>
            {portal?.data?.settings?.retailerUrl}
          </p>
        </div>
      )}
    </>
  );
}
